import { TippyProps } from '@tippyjs/react'
import CharacterCount from '@tiptap/extension-character-count'
import { TaskItem } from '@tiptap/extension-task-item'
import { TaskList } from '@tiptap/extension-task-list'
import Underline from '@tiptap/extension-underline'
import { Editor, EditorEvents, Extensions, generateHTML, generateText } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import React, { useMemo } from 'react'

import { BASE_TEXT_COLOR } from './constants'

import ImageView from '~common/RichTextEditor/ImageView'
import UploadPlaceholder from '~common/RichTextEditor/UploadPlaceholder'
import VideoView from '~common/RichTextEditor/VideoView'
import CheckListIcon from '~icons/editor/checklist.svg'
import H1Icon from '~icons/editor/h1.svg'
import H2Icon from '~icons/editor/h2.svg'
import H3Icon from '~icons/editor/h3.svg'
import H4Icon from '~icons/editor/h4.svg'
import ImageIcon from '~icons/editor/image.svg'
import NumericListIcon from '~icons/editor/ol.svg'
import ParagraphIcon from '~icons/editor/p.svg'
import QuoteIcon from '~icons/editor/quote.svg'
import TableIcon from '~icons/editor/table.svg'
import BulletListIcon from '~icons/editor/ul.svg'
import GrammarIcon from '~icons/grammar.svg'
import LanguageIcon from '~icons/language.svg'
import LaunchBriefIcon from '~icons/launchBrief-md.svg'
import WriteIcon from '~icons/write.svg'
import { BackgroundColor } from '~lib/editor/extensions/background-color'
import { Color } from '~lib/editor/extensions/color'
import { Commands, Mentions } from '~lib/editor/extensions/commands'
import { SuggestionItem } from '~lib/editor/extensions/commands/getSuggestionItems'
import { Link } from '~lib/editor/extensions/link'
import mediaExtensions from '~lib/editor/extensions/media'
import { Placeholder } from '~lib/editor/extensions/placeholder'
import { Table, TableCell, TableHeader, TableRow } from '~lib/editor/extensions/table'
import { CustomTextStyle } from '~lib/editor/extensions/text-style'
import { capitalize } from '~lib/textFormat'
import { convertFileListToArray } from '~lib/utils/files'
import useContinueWriting from '~modules/aiAssist/usecases/useContinueWriting'

export enum NODE {
  H1 = 'heading-1',
  H2 = 'heading-2',
  H3 = 'heading-3',
  H4 = 'heading-4',
  P = 'paragraph',
  UL = 'bulletList',
  OL = 'orderedList',
  TASK_LIST = 'taskList',
  QUOTE = 'blockquote',
}

export interface INode {
  name: NODE
  label: string
  icon: JSX.Element
}

export const nodesMap: Record<NODE, INode> = {
  [NODE.H1]: {
    name: NODE.H1,
    label: 'H1 Title',
    icon: <H1Icon />,
  },
  [NODE.H2]: {
    name: NODE.H2,
    label: 'H2 Title',
    icon: <H2Icon />,
  },
  [NODE.H3]: {
    name: NODE.H3,
    label: 'H3 Title',
    icon: <H3Icon />,
  },
  [NODE.H4]: {
    name: NODE.H4,
    label: 'H4 Title',
    icon: <H4Icon />,
  },
  [NODE.P]: {
    name: NODE.P,
    label: capitalize(NODE.P),
    icon: <ParagraphIcon />,
  },
  [NODE.OL]: {
    name: NODE.OL,
    label: 'Ordered list',
    icon: <NumericListIcon />,
  },
  [NODE.UL]: {
    name: NODE.UL,
    label: 'Bullet list',
    icon: <BulletListIcon />,
  },
  [NODE.TASK_LIST]: {
    name: NODE.TASK_LIST,
    label: 'Task list',
    icon: <CheckListIcon />,
  },
  [NODE.QUOTE]: {
    name: NODE.QUOTE,
    label: capitalize(NODE.QUOTE),
    icon: <QuoteIcon />,
  },
}

const orderedNodes: INode[] = Object.values(nodesMap)
const headings = new Set([NODE.H1, NODE.H2, NODE.H3, NODE.H4])
const lists = new Set([NODE.OL, NODE.UL, NODE.TASK_LIST, NODE.TASK_LIST])

/**
 * Base editor json structure
 */

export const editorJSON = (): IEditorContent => {
  return {
    type: 'doc',
    content: [],
  }
}

/**
 * Editor paragraph structure.
 * If required, can be extended with marks props.
 */
export function paragraphJSON(text: string): EditorParagraph {
  return {
    type: 'paragraph',
    content: [{ type: 'text', text }],
  }
}
/**
 * Editor heading structure.
 */
export function headingJSON(text: string, level?: HeadingLevel): EditorHeading {
  return {
    type: 'heading',
    content: [{ type: 'text', text }],
    attrs: level ? { level } : undefined,
  }
}

export const prepareContent = (content: string): string => {
  try {
    return JSON.parse(content)
  } catch {
    return content
  }
}

export const useNodes = (editor: Editor) => {
  return useMemo(() => {
    return {
      nodes: orderedNodes,
      toggleNode: (name: NODE) => {
        // we can't toggle the list to any node type except paragraph
        // it is possible that this is the expected behavior, but not in our case
        const isList = editor.isActive('bulletList') || editor.isActive('orderedList') || editor.isActive('taskList')
        const isListCommand = lists.has(name)
        // TODO: maybe would be better create custom nodes or extension and rewrite existing commands to handle this and other cases?
        const commandsChain = isList && !isListCommand ? editor.chain().focus().clearNodes() : editor.chain().focus()

        if (headings.has(name)) {
          const [, level] = name.split('-')
          commandsChain.toggleHeading({ level: Number(level) as HeadingLevel }).run()
          return
        }

        if (name === NODE.P) {
          commandsChain.setParagraph().run()
          return
        }

        commandsChain[`toggle${name[0]?.toUpperCase() + name.slice(1)}`]().run()
      },
      isActive: (nodeName: NODE): boolean => {
        if (headings.has(nodeName)) {
          const [name, level] = nodeName.split('-')
          return editor.isActive(name, { level: Number(level) as HeadingLevel })
        }
        return editor.isActive(nodeName)
      },
      getActiveNode: () => {
        const activeNode = orderedNodes.find(({ name }) => {
          if (name === NODE.P) return false // skip paragraph

          if (headings.has(name)) {
            const [nodeName, level] = name.split('-')
            return editor.isActive(nodeName, { level: Number(level) })
          }
          return editor.isActive(name)
        })

        return activeNode || nodesMap[NODE.P]
      },
    }
  }, [editor])
}

/**
 * Allowe to create remove event listener with anonimus function
 */
export const addEditorEventListener = <E extends keyof EditorEvents>(
  editor: Editor,
  event: E,
  callback: (response: EditorEvents[E]) => void
) => {
  // TODO resolve types conflicts between tiptap and prosemirror
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  editor?.on(event, callback)
  return () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    editor?.off(event, callback)
  }
}

export const createLocalUrl = (file: File) => Promise.resolve(URL.createObjectURL(file))

export const tippyOptions: Omit<TippyProps, 'render' | 'content'> = {
  placement: 'top-start',
  maxWidth: 'none',
  interactive: true,
  hideOnClick: true,
}

export type EditorMenuExtensionsOptions = {
  mediaEnabled?: boolean
  tableEnabled?: boolean
  aiAssistEnabled?: boolean
  toggleLoading: (value: boolean) => void
}

export function useEditorMenuExtensions({
  tableEnabled,
  mediaEnabled,
  aiAssistEnabled,
  toggleLoading,
}: EditorMenuExtensionsOptions) {
  const { continueWriting } = useContinueWriting()

  return useMemo(() => {
    const extraItems: SuggestionItem[] = []

    if (tableEnabled) {
      extraItems.push({
        label: 'Table',
        icon: <TableIcon />,
        isVisible: (editor) => !editor.isActive('table'),
        command: ({ editor, range }) => {
          editor.commands.focus()

          requestAnimationFrame(() => {
            const { from } = editor.state.selection
            editor.chain().deleteRange(range).insertContent(' ').run()

            editor
              .chain()
              .focus(from - 1)
              .insertTable({ cols: 3, rows: 3, withHeaderRow: false })
              .run()
          })
        },
      })
    }

    if (mediaEnabled) {
      extraItems.push({
        label: 'Image',
        icon: <ImageIcon />,
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run()

          const input = document.createElement('input')
          input.type = 'file'
          input.style.display = 'none'

          input.addEventListener('change', () => {
            const images = convertFileListToArray(input.files)
            editor.commands.insertImages(images, {
              onSuccess: () => {
                editor.chain().focus().enter().run()
                input.remove()
              },
            })
          })

          input.addEventListener('blur', () => {
            input.remove()
          })

          document.body.append(input)
          input.click()
        },
      })
    }
    // TODO
    if (aiAssistEnabled) {
      extraItems.push(
        {
          icon: <WriteIcon />,
          label: 'Continue writing',
          command: async ({ editor, range }) => {
            const { anchor } = editor.state.selection

            const text = editor.state.doc.textBetween(0, anchor, ' ')

            const sanitizedText = text
              .slice(0, -1) // remove slash char
              .trim()

            if (!sanitizedText) return
            editor.setEditable(false)
            toggleLoading(true)
            try {
              const generatedText = await continueWriting(sanitizedText)
              editor.chain().focus().deleteRange(range).insertContent(paragraphJSON(generatedText)).run()
            } finally {
              editor.setEditable(true)
              toggleLoading(false)
            }
          },
        },
        {
          icon: <LaunchBriefIcon />,
          label: 'Summarize',
          disabled: true,
          hint: 'Please highlight text first',
          command: () => false,
        },
        {
          icon: <GrammarIcon />,
          label: 'Check grammar',
          disabled: true,
          hint: 'Please highlight text first',
          command: () => false,
        },
        {
          icon: <LanguageIcon />,
          label: 'Translate',
          disabled: true,
          hint: 'Please highlight text first',
          command: () => false,
        }
      )
    }

    return [
      Commands.configure({
        extraItems,
      }),
      Mentions.configure({
        extraItems,
      }),
    ]
  }, [aiAssistEnabled, continueWriting, mediaEnabled, toggleLoading, tableEnabled])
}

export type TextEditorExtensionsOptions = {
  historyDisabled?: boolean
  mediaEnabled?: boolean
  tableEnabled?: boolean
  uploadFile?: (file: File) => Promise<string>
  placeholder?: string
  extraExtensions?: Extensions
}

export function getEditorContentExtensions({
  uploadFile = createLocalUrl,
  historyDisabled = false,
  mediaEnabled = false,
  tableEnabled = false,
  placeholder = undefined,
  extraExtensions = [],
}: TextEditorExtensionsOptions = {}): Extensions {
  return [
    StarterKit.configure({
      history: historyDisabled ? false : {},
      codeBlock: false,
      code: false,
      dropcursor: { color: BASE_TEXT_COLOR },
    }),
    ...mediaExtensions({
      enabled: mediaEnabled,
      upload: { uploadFile, PlaceholderComponent: UploadPlaceholder },
      image: { Component: ImageView },
      video: { Component: VideoView },
    }),
    CharacterCount.configure(),
    CustomTextStyle,
    Color,
    BackgroundColor,
    Underline,
    Link.configure({ openOnClick: true, linkOnPaste: true }),
    ...(tableEnabled ? [Table.configure({ resizable: true }), TableRow, TableHeader, TableCell] : []),
    Placeholder.configure({
      placeholder: ({ node, editor }) => {
        if (editor.isEmpty && placeholder) return placeholder

        switch (node.type.name) {
          case 'heading':
            return `Header ${node.attrs.level}`
          case 'paragraph':
            return 'Paragraph'
          case 'blockquote':
            return 'Blockquote'
          default:
            return null
        }
      },
      emptyNodeClass: 'empty',
      showOnlyWhenEditable: false,
      showOnlyCurrent: true,
    }),
    TaskList,
    TaskItem,
    ...extraExtensions,
  ]
}

/**
 * Generate html requires all extensions for nodes used in Wysiwyg editor
 */
export const generateHTMLFromRichTextEditorContent = (content: string, options?: TextEditorExtensionsOptions) => {
  try {
    const json = JSON.parse(content)
    return generateHTML(json, getEditorContentExtensions(options))
  } catch {
    return ''
  }
}

/**
 * Generate text requires all extensions for nodes used in Wysiwyg editor
 */
export const generateTextFromRichTextEditorContent = (content: string, options?: TextEditorExtensionsOptions) => {
  try {
    const json = JSON.parse(content)
    return generateText(json, getEditorContentExtensions(options))
  } catch {
    return ''
  }
}
